import React, { useRef } from 'react';
import { PosStockNoticeModal } from './PosStockNoticeModal';
import { PosWeightQuantityModal } from './PosWeightQuantityModal';
import { PosHourQuantityModal } from '../PosHourQuantityModal';
import { PosVariantPickerModal } from './PosVariantPickerModal';
import { PosMenuOptionsModal } from './PosMenuOptionsModal';
import { hapticAddToCart } from '../../../shared/ui/posHaptics';
import type { Product } from '../../../catalog/domain-api'
import type { ProductVariant } from '../../../catalog/domain-api';
import type { MenuOptionGroup, SelectedMenuOption } from '../../../catalog/domain-api';
import { formatUnitLabel } from '../../../catalog/domain-api';
import type { PosStockSalePolicy } from '../../domain/posTypes';
import { useBusinessProfileStore } from '../../../shell/session/store/useBusinessProfileStore'
import { allowsWeightSales } from '../../../shell/feature-flags/businessCapabilitiesService'
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService'
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider';

type StockTapNotice = { product: Product; policy: PosStockSalePolicy };

type StockTapNoticeCopy = {
  variant: PosStockSalePolicy;
  title: string;
  body: string;
  canAdd: boolean;
};

export type PosProductPressModalsProps = {
  stockTapNotice: StockTapNotice | null;
  setStockTapNotice: (notice: StockTapNotice | null) => void;
  stockTapNoticeCopy: StockTapNoticeCopy | null;
  handleAddOutOfStockProduct: () => void;
  openInventoryForProduct: (product: Product) => void;

  weightProduct: Product | null;
  setWeightProduct: (product: Product | null) => void;
  hourProduct: Product | null;
  setHourProduct: (product: Product | null) => void;
  variantPickerProduct: Product | null;
  setVariantPickerProduct: (product: Product | null) => void;
  variantPickerVariants: ProductVariant[];
  setVariantPickerVariants: (variants: ProductVariant[]) => void;
  pendingVariantId: string | null;
  setPendingVariantId: (id: string | null) => void;
  handleVariantSelect: (variant: ProductVariant) => void;
  menuOptionsProduct: Product | null;
  setMenuOptionsProduct: (product: Product | null) => void;
  menuOptionsGroups: MenuOptionGroup[];
  menuOptionsLoading?: boolean;
  /** Gap 4: pre-selección al editar una línea existente. */
  initialMenuOptionsSelected?: SelectedMenuOption[];
  initialMenuOptionsLineNote?: string | null;
  handleMenuOptionsConfirm: (selected: SelectedMenuOption[], lineNote: string) => void;

  currentBranchId?: string | null;
  stockSalePolicy?: PosStockSalePolicy;
  addProductToCart: (
    product: Product,
    quantityOrOpts?: number | import('../../store/usePosStore').AddProductToCartOptions,
  ) => void;
};

export const PosProductPressModals = ({
  stockTapNotice,
  setStockTapNotice,
  stockTapNoticeCopy,
  handleAddOutOfStockProduct,
  openInventoryForProduct,
  weightProduct,
  setWeightProduct,
  hourProduct,
  setHourProduct,
  variantPickerProduct,
  setVariantPickerProduct,
  variantPickerVariants,
  setVariantPickerVariants,
  pendingVariantId,
  setPendingVariantId,
  handleVariantSelect,
  menuOptionsProduct,
  setMenuOptionsProduct,
  menuOptionsGroups,
  menuOptionsLoading = false,
  initialMenuOptionsSelected,
  initialMenuOptionsLineNote,
  handleMenuOptionsConfirm,
  currentBranchId,
  stockSalePolicy,
  addProductToCart,
}: PosProductPressModalsProps) => {
  const isConfirmingRef = useRef(false);
  const { flags } = useGlobalFeatureFlags();
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const scaleFeatureEnabled =
    allowsWeightSales(businessProfile) && isGlobalFeatureOn(flags, 'scale_integration');

  // Gap 1: heading dinámico según UoM. kg/g → "Cantidad por peso";
  // l/ml/m/cm → "Ingresar cantidad" (no es peso, es volumen/longitud).
  const weightHeading = (() => {
    const uom = weightProduct?.unitOfMeasure ?? 'kg';
    if (uom === 'kg' || uom === 'g') return 'Cantidad por peso';
    return 'Ingresar cantidad';
  })();
  return (
    <>
      <PosStockNoticeModal
        visible={stockTapNotice != null}
        variant={stockTapNoticeCopy?.variant ?? 'warn'}
        title={stockTapNoticeCopy?.title ?? 'Sin stock'}
        body={stockTapNoticeCopy?.body ?? ''}
        canAdd={stockTapNoticeCopy?.canAdd ?? false}
        onClose={() => setStockTapNotice(null)}
        onCorrectStock={() => {
          if (stockTapNotice) openInventoryForProduct(stockTapNotice.product);
        }}
        onAddAnyway={handleAddOutOfStockProduct}
      />

      <PosWeightQuantityModal
        visible={weightProduct != null}
        product={weightProduct}
        heading={weightHeading}
        branchId={currentBranchId}
        scaleFeatureEnabled={scaleFeatureEnabled}
        onCancel={() => setWeightProduct(null)}
        onConfirm={(quantity) => {
          if (isConfirmingRef.current) return;
          isConfirmingRef.current = true;
          try {
          if (weightProduct) {
            hapticAddToCart();
            addProductToCart(
              weightProduct,
              pendingVariantId ? { quantity, variantId: pendingVariantId } : quantity,
            );
          }
          setWeightProduct(null);
          setPendingVariantId(null);
          } finally {
            isConfirmingRef.current = false;
          }
        }}
      />

      <PosHourQuantityModal
        visible={hourProduct != null}
        product={hourProduct}
        onCancel={() => setHourProduct(null)}
        onConfirm={(quantity) => {
          if (isConfirmingRef.current) return;
          isConfirmingRef.current = true;
          try {
          if (hourProduct) {
            hapticAddToCart();
            addProductToCart(
              hourProduct,
              pendingVariantId ? { quantity, variantId: pendingVariantId } : quantity,
            );
          }
          setHourProduct(null);
          setPendingVariantId(null);
          } finally {
            isConfirmingRef.current = false;
          }
        }}
      />

      <PosVariantPickerModal
        visible={variantPickerProduct != null}
        product={variantPickerProduct}
        variants={variantPickerVariants}
        branchId={currentBranchId}
        stockSalePolicy={stockSalePolicy}
        onCancel={() => {
          setVariantPickerProduct(null);
          setVariantPickerVariants([]);
        }}
        onSelect={(variant) => {
          if (isConfirmingRef.current) return;
          if (!variantPickerProduct) return;
          isConfirmingRef.current = true;
          try {
          handleVariantSelect(variant);
          } finally {
            isConfirmingRef.current = false;
          }
        }}
      />

      <PosMenuOptionsModal
        visible={menuOptionsProduct != null}
        product={menuOptionsProduct}
        groups={menuOptionsGroups}
        loading={menuOptionsLoading}
        initialSelected={initialMenuOptionsSelected}
        initialLineNote={initialMenuOptionsLineNote}
        onCancel={() => {
          setMenuOptionsProduct(null);
        }}
        onConfirm={handleMenuOptionsConfirm}
      />
    </>
  );
};
